import React, { createContext, useState, useEffect, useContext } from 'react';
import { useUser } from './UserContext';

// 创建上下文
const CultivationContext = createContext();

// 自定义钩子，方便其他组件使用上下文
export const useCultivation = () => useContext(CultivationContext);

// 修炼境界定义
const CULTIVATION_LEVELS = [
  { id: 1, name: '筑基期', description: '修行起步，感知天地灵气', requiredExp: 0, color: 'cultivation-level-1' },
  { id: 2, name: '练气期', description: '体内开始积累灵力', requiredExp: 1000, color: 'cultivation-level-2' },
  { id: 3, name: '炼神期', description: '神识增强，意念可操控物体', requiredExp: 3000, color: 'cultivation-level-3' },
  { id: 4, name: '金丹期', description: '体内凝结金丹，寿命大增', requiredExp: 8000, color: 'cultivation-level-4' },
  { id: 5, name: '元婴期', description: '元婴出窍，可短暂离体', requiredExp: 20000, color: 'cultivation-level-5' },
  { id: 6, name: '化神期', description: '化形为神，接近天人', requiredExp: 50000, color: 'cultivation-level-6' },
  { id: 7, name: '炼虚期', description: '虚实转换，瞬息千里', requiredExp: 120000, color: 'cultivation-level-7' },
  { id: 8, name: '合体期', description: '天人合一，掌控自然规律', requiredExp: 300000, color: 'cultivation-level-8' },
  { id: 9, name: '渡劫期', description: '即将飞升，面临天劫考验', requiredExp: 800000, color: 'cultivation-level-9' },
  { id: 10, name: '飞升期', description: '超脱凡尘，飞升成仙', requiredExp: 2000000, color: 'cultivation-level-9' }
];

// 修炼技能定义
const CULTIVATION_SKILLS = [
  { id: 'physical_training', name: '体魄锻炼', description: '提升身体素质和生命力', attribute: 'constitution', baseExp: 10 },
  { id: 'meditation', name: '冥想修炼', description: '增强精神力和灵力恢复', attribute: 'spirit', baseExp: 15 },
  { id: 'energy_control', name: '灵力控制', description: '提高灵力使用效率', attribute: 'control', baseExp: 20 },
  { id: 'martial_arts', name: '武技修炼', description: '提升战斗能力和技巧', attribute: 'strength', baseExp: 12 },
  { id: 'alchemy', name: '丹药炼制', description: '学习炼制各种丹药', attribute: 'intelligence', baseExp: 25 },
  { id: 'artifact_crafting', name: '法器锻造', description: '学习打造法器', attribute: 'dexterity', baseExp: 30 },
  { id: 'talisman_drawing', name: '符箓绘制', description: '学习绘制各种符箓', attribute: 'perception', baseExp: 22 },
  { id: 'divination', name: '占卜预测', description: '学习预测未来和寻找机缘', attribute: 'wisdom', baseExp: 18 },
  { id: 'cyber_cultivation', name: '赛博修炼', description: '现代科技与修炼的结合', attribute: 'technology', baseExp: 35 }
];

// 丹药定义
const ALCHEMY_PILLS = [
  { id: 'foundation_pill', name: '筑基丹', description: '稳固根基，提升修炼速度', effect: { expMultiplier: 1.2 }, cost: 50, level: 1 },
  { id: 'spirit_gathering_pill', name: '聚灵丹', description: '加快灵力吸收', effect: { spiritRecovery: 10 }, cost: 100, level: 2 },
  { id: 'body_refining_pill', name: '炼体丹', description: '增强体魄，提高生命力', effect: { constitutionBoost: 5 }, cost: 150, level: 3 },
  { id: 'mind_clearing_pill', name: '清心丹', description: '净化心灵，提高精神力', effect: { wisdomBoost: 5 }, cost: 200, level: 4 },
  { id: 'breakthrough_pill', name: '突破丹', description: '辅助突破瓶颈', effect: { breakthroughChance: 0.2 }, cost: 500, level: 5 }
];

// 上下文提供者组件
export const CultivationProvider = ({ children }) => {
  const { user, updateProfile } = useUser();
  
  // 修炼状态
  const [cultivation, setCultivation] = useState({
    level: 1,
    experience: 0,
    skills: {},
    attributes: {
      constitution: 10,
      spirit: 10,
      control: 10,
      strength: 10,
      intelligence: 10,
      dexterity: 10,
      perception: 10,
      wisdom: 10,
      technology: 10
    },
    pills: [],
    artifacts: []
  });
  
  // 当前的修炼境界
  const [currentLevel, setCurrentLevel] = useState(CULTIVATION_LEVELS[0]);
  const [nextLevel, setNextLevel] = useState(CULTIVATION_LEVELS[1]);
  
  // 加载用户的修炼数据
  useEffect(() => {
    if (user) {
      // 从本地存储或用户数据中加载修炼信息
      const storedCultivation = localStorage.getItem(`cultivation_${user.id}`);
      if (storedCultivation) {
        const parsedCultivation = JSON.parse(storedCultivation);
        setCultivation(parsedCultivation);
        
        // 设置当前和下一个境界
        const currentLevelData = CULTIVATION_LEVELS.find(l => l.id === parsedCultivation.level) || CULTIVATION_LEVELS[0];
        setCurrentLevel(currentLevelData);
        
        const nextLevelData = CULTIVATION_LEVELS.find(l => l.id === parsedCultivation.level + 1);
        if (nextLevelData) {
          setNextLevel(nextLevelData);
        }
      } else {
        // 创建新的修炼数据
        const initialCultivation = {
          level: 1,
          experience: 0,
          skills: {},
          attributes: {
            constitution: 10,
            spirit: 10,
            control: 10,
            strength: 10,
            intelligence: 10,
            dexterity: 10,
            perception: 10,
            wisdom: 10,
            technology: 10
          },
          pills: [],
          artifacts: []
        };
        
        setCultivation(initialCultivation);
        setCurrentLevel(CULTIVATION_LEVELS[0]);
        setNextLevel(CULTIVATION_LEVELS[1]);
        
        // 保存初始修炼数据
        localStorage.setItem(`cultivation_${user.id}`, JSON.stringify(initialCultivation));
      }
    }
  }, [user]);
  
  // 保存修炼数据
  const saveCultivation = (newCultivation) => {
    if (user) {
      setCultivation(newCultivation);
      localStorage.setItem(`cultivation_${user.id}`, JSON.stringify(newCultivation));
      
      // 更新用户资料中的等级
      updateProfile({ level: newCultivation.level });
      
      // 更新当前和下一个境界
      const currentLevelData = CULTIVATION_LEVELS.find(l => l.id === newCultivation.level) || CULTIVATION_LEVELS[0];
      setCurrentLevel(currentLevelData);
      
      const nextLevelData = CULTIVATION_LEVELS.find(l => l.id === newCultivation.level + 1);
      if (nextLevelData) {
        setNextLevel(nextLevelData);
      }
    }
  };
  
  // 获取修炼进度百分比
  const getCultivationProgress = () => {
    if (!nextLevel) return 100; // 已经是最高境界
    
    const currentExp = cultivation.experience - currentLevel.requiredExp;
    const requiredExp = nextLevel.requiredExp - currentLevel.requiredExp;
    
    return Math.floor((currentExp / requiredExp) * 100);
  };
  
  // 增加修炼经验
  const addExperience = (amount) => {
    if (!user) return false;
    
    let newExperience = cultivation.experience + amount;
    let newLevel = cultivation.level;
    
    // 检查是否升级
    while (nextLevel && newExperience >= nextLevel.requiredExp) {
      newLevel++;
      
      // 检查是否已经达到最高境界
      if (newLevel >= CULTIVATION_LEVELS.length) {
        newLevel = CULTIVATION_LEVELS.length;
        break;
      }
    }
    
    // 更新修炼数据
    const newCultivation = {
      ...cultivation,
      level: newLevel,
      experience: newExperience
    };
    
    saveCultivation(newCultivation);
    
    return newLevel > cultivation.level; // 返回是否升级
  };
  
  // 练习技能
  const practiceSkill = (skillId) => {
    if (!user) return false;
    
    const skill = CULTIVATION_SKILLS.find(s => s.id === skillId);
    if (!skill) return false;
    
    // 计算获得的经验
    const baseExp = skill.baseExp;
    const attributeValue = cultivation.attributes[skill.attribute] || 10;
    const expGain = baseExp * (1 + attributeValue / 100);
    
    // 更新技能经验
    const currentSkillExp = cultivation.skills[skillId] || 0;
    const newSkillExp = currentSkillExp + expGain;
    
    // 更新修炼数据
    const newCultivation = {
      ...cultivation,
      skills: {
        ...cultivation.skills,
        [skillId]: newSkillExp
      }
    };
    
    saveCultivation(newCultivation);
    
    // 增加总经验值的一部分
    addExperience(expGain / 2);
    
    return {
      skillExp: newSkillExp,
      expGain,
      totalExp: cultivation.experience + expGain / 2
    };
  };
  
  // 使用丹药
  const usePill = (pillId) => {
    if (!user) return false;
    
    const pill = ALCHEMY_PILLS.find(p => p.id === pillId);
    if (!pill) return false;
    
    // 应用丹药效果
    let newCultivation = { ...cultivation };
    
    if (pill.effect.expMultiplier) {
      // 经验加成，立即增加一定经验
      const expGain = cultivation.experience * (pill.effect.expMultiplier - 1);
      addExperience(expGain);
    }
    
    if (pill.effect.constitutionBoost) {
      newCultivation.attributes.constitution += pill.effect.constitutionBoost;
    }
    
    if (pill.effect.wisdomBoost) {
      newCultivation.attributes.wisdom += pill.effect.wisdomBoost;
    }
    
    // 记录使用的丹药
    newCultivation.pills.push({
      id: pillId,
      usedAt: new Date().toISOString()
    });
    
    saveCultivation(newCultivation);
    
    return true;
  };
  
  // 提供的上下文值
  const value = {
    cultivation,
    currentLevel,
    nextLevel,
    getCultivationProgress,
    addExperience,
    practiceSkill,
    usePill,
    CULTIVATION_LEVELS,
    CULTIVATION_SKILLS,
    ALCHEMY_PILLS
  };
  
  return <CultivationContext.Provider value={value}>{children}</CultivationContext.Provider>;
};
